import { useLayoutEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';

import { useAppDispatch } from 'src/modules/Redux/store';
import { setItemHeight } from 'src/modules/Redux';
import { useAnnouncer } from 'src/ui/pages/CustomerPickupScreen/hooks/useAnnouncer';
import { getAnnouncement } from 'src/ui/pages/CustomerPickupScreen/lib';
import { IPurchase } from '@snackpass/snackpass-types';
import {
    getFirstName,
    getMinutesLeft,
    normalizedOrderStatus,
    transactionSource,
} from 'src/modules/Utils/helpers';
import { useChannelOptions } from 'src/ui/pages/CustomerPickupScreen/hooks/useChannelOptions';
import { MobileIcon } from '@radix-ui/react-icons';

type Props = {
    index?: number;
    purchase: IPurchase;
};

const PhoneIcon: React.FC<{ color?: string; hidden?: boolean }> = props => {
    if (props.hidden) return null;
    return (
        <MobileIcon
            width="1.5em"
            height="1.5em"
            fill={props.color || '#606C76'}
        />
    );
};

const ScooterIcon: React.FC<{ color?: string; hidden?: boolean }> = props => {
    if (props.hidden) return null;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1.5em"
            height="1.5em"
            viewBox="0 0 135 135"
            fill="none"
        >
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M93.9013 28.125C99.0465 28.125 100.073 28.2195 100.825 28.5153C101.702 28.86 102.477 29.4191 103.081 30.1415C103.598 30.7594 104.023 31.7324 105.679 36.7011C108.405 44.8767 109.527 48.3101 110.012 51.7111C110.458 54.8335 110.526 57.9982 110.217 61.137C109.88 64.5573 108.925 67.9739 106.597 76.1219L105.196 81.0247C103.721 86.1886 103.328 87.1936 102.812 87.8506C102.21 88.6171 101.418 89.2138 100.516 89.5824C99.7424 89.8984 98.6681 90 93.2976 90H70.6668C70.8885 89.372 71.0748 88.7259 71.2231 88.064C71.4021 87.2647 71.511 86.3906 71.637 85.3795L71.637 85.3794L71.6371 85.3792L71.6371 85.3791L71.6645 85.1596L73.365 71.5553L73.3938 71.3246L73.3939 71.3244C73.8064 68.0252 74.1595 65.2011 74.2504 62.872C74.3458 60.4318 74.1905 57.9666 73.2136 55.5501C71.7701 51.9798 69.149 49.0106 65.7853 47.1355C63.5086 45.8664 61.0817 45.4065 58.6485 45.1984C56.326 44.9998 53.4799 44.9999 50.1549 45L49.9224 45H42.8901L42.6576 45C39.3326 44.9999 36.4865 44.9998 34.164 45.1984C31.7308 45.4065 29.3039 45.8664 27.0272 47.1355C23.6635 49.0106 21.0424 51.9798 19.599 55.5501C18.622 57.9666 18.4667 60.4318 18.5621 62.872C18.653 65.2012 19.0062 68.0253 19.4187 71.3246L19.4475 71.5553L19.8413 74.7054C17.0346 75.8168 14.4684 77.4864 12.3046 79.6261C9.28867 82.6084 7.17712 86.3824 6.21345 90.5129C5.24977 94.6434 5.47322 98.9622 6.85803 102.971C8.24283 106.98 10.7326 110.516 14.0402 113.171C17.3478 115.826 21.3385 117.492 25.5521 117.977C29.7657 118.462 34.0306 117.747 37.8549 115.912C41.6792 114.078 44.9072 111.201 47.1667 107.611C48.4103 105.635 49.3326 103.488 49.9105 101.25H53.4375L53.6588 101.25L54.0813 101.25H85.0895C85.8077 104.032 87.0549 106.661 88.7741 108.989C91.1876 112.258 94.4414 114.813 98.1899 116.381C101.938 117.95 106.042 118.474 110.064 117.898C114.086 117.322 117.878 115.667 121.036 113.11C124.193 110.553 126.6 107.188 127.999 103.373C129.398 99.5582 129.738 95.4355 128.983 91.4429C128.228 87.4503 126.405 83.7367 123.709 80.6965C122.135 78.9209 120.296 77.4136 118.268 76.2225C119.976 70.2114 121.015 66.2718 121.413 62.2409C121.811 58.2053 121.722 54.1365 121.149 50.1219C120.736 47.2274 119.982 44.3684 118.843 40.7363C119.197 40.6903 119.552 40.6098 119.904 40.4926C122.851 39.5102 124.444 36.3247 123.461 33.3775L120.649 24.94C119.666 21.9928 116.481 20.4 113.534 21.3824C112.732 21.6496 112.031 22.0798 111.454 22.6239C109.681 20.602 107.449 19.0316 104.943 18.0459C101.936 16.8634 98.5619 16.8685 94.7173 16.8742L93.9013 16.875H90C86.8934 16.875 84.375 19.3934 84.375 22.5C84.375 25.6066 86.8934 28.125 90 28.125H93.9013ZM97.8245 102.307C97.5698 101.962 97.3358 101.604 97.1232 101.234C99.9239 101.177 102.462 100.94 104.77 99.9973C107.477 98.8915 109.85 97.1012 111.657 94.8018C113.198 92.842 114.123 90.4667 114.948 87.7895C115.065 87.9105 115.18 88.0342 115.292 88.1608C116.64 89.6809 117.551 91.5377 117.929 93.534C118.307 95.5303 118.137 97.5916 117.437 99.499C116.737 101.406 115.534 103.089 113.955 104.368C112.376 105.646 110.481 106.473 108.47 106.761C106.458 107.049 104.407 106.787 102.532 106.003C100.658 105.219 99.0313 103.942 97.8245 102.307ZM37.9175 101.163C37.8307 101.317 37.7401 101.468 37.6459 101.618C36.5161 103.413 34.9021 104.852 32.9899 105.769C31.0778 106.686 28.9453 107.044 26.8385 106.801C24.7317 106.559 22.7364 105.726 21.0826 104.398C19.4288 103.071 18.1839 101.303 17.4915 99.2981C16.7991 97.2936 16.6874 95.1342 17.1692 93.069C17.6511 91.0037 18.7068 89.1167 20.2148 87.6256C20.5917 87.2529 20.9929 86.9088 21.4152 86.595C21.472 86.9156 21.5308 87.226 21.592 87.5261C21.9982 89.5153 22.5895 91.4666 23.7435 93.3143C25.4797 96.094 27.9877 98.3081 30.9613 99.686C32.9379 100.602 34.9475 100.947 36.9717 101.103C37.2771 101.127 37.5924 101.146 37.9175 101.163Z"
                fill={props.color || '#606C76'}
            />
        </svg>
    );
};

const Tile: React.FC<Props> = ({ index, purchase }) => {
    const dispatch = useAppDispatch();
    const itemRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const { channelOptions } = useChannelOptions();
    useLayoutEffect(() => {
        if (index === 0 && itemRef.current) {
            dispatch(setItemHeight(itemRef.current.offsetHeight));
        }
    }, [dispatch, index]);
    const { announce } = useAnnouncer();
    const orderSource = transactionSource(purchase);
    const orderNumber = purchase.orderNumber ?? purchase.receiptNumber;
    const status = normalizedOrderStatus(purchase);
    const minutesLeft = getMinutesLeft(purchase);
    const onlyShowFirstInitial = channelOptions.getOrDefault(
        'onlyShowFirstInitial',
        false
    );

    const isThirdParty = purchase.transactionSource === 'thirdParty';
    const customerName = purchase.user?.name;
    const thirdPartyCustomerName =
        purchase.thirdPartyOrderInfo?.deliverect?.user?.name;

    const useName = isThirdParty ? thirdPartyCustomerName : customerName;

    const customerFirstName = getFirstName(useName, onlyShowFirstInitial);

    // TODO move to special field called Hide order after ready (minutes)
    const shouldShowMinutesLeft =
        status !== 'Ready' && !!minutesLeft && minutesLeft <= 25;

    const _handleClick = () => {
        const message = getAnnouncement(orderNumber, customerFirstName);
        announce(message);
    };
    const CUTOFF = 14;
    const customerFirstNameTruncated =
        customerFirstName.length > CUTOFF
            ? `${customerFirstName.substring(0, CUTOFF)}..`
            : customerFirstName;

    return (
        <div
            ref={itemRef}
            className={'box-border'}
            style={{
                padding: '1.25em',
                paddingTop: '1em',
                paddingBottom: '1em',
                border: `1px solid ${theme.background}`,
            }}
            onClick={_handleClick}
        >
            <div className="flex flex-row justify-between items-center">
                <div className="flex w-full flex-col">
                    <div className="flex items-center justify-start">
                        <div className="mr-1">
                            <p
                                className="font-bold"
                                style={{
                                    fontSize: '2em',
                                    lineHeight: '1em',
                                }}
                            >
                                {orderNumber}
                            </p>
                        </div>
                        <PhoneIcon hidden={orderSource !== 'App'} />
                        <ScooterIcon hidden={!isThirdParty} />
                    </div>
                    <div className="flex items-center justify-start w-full">
                        <p
                            style={{
                                color: '#606C76',
                                fontSize: '1.2em',
                            }}
                        >
                            {customerFirstNameTruncated}
                            {shouldShowMinutesLeft && ` • ${minutesLeft}m`}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Tile;
